import { useState, useEffect, useCallback, useRef } from 'react'
import { snapshot } from 'valtio'
import store from '@/store'
import { Theme } from '@/types/theme'
import { ProductData } from '@/types/product'
import updateTheme from '@/services/ThemeService/updateTheme'
import getAllProducts from '@/services/ProductService/getAllProducts'
import notifyError from '@/utils/notifyError'

interface ThemeCustomizerModalProps {
  theme: Theme
  sku?: string
  isOpen: boolean
  onClose: () => void
  onThemeSaved?: () => void
}

const getBaseUrl = (): string => {
  const searchParams = new URLSearchParams(window.location.search)
  const urlParam =
    searchParams.get('customizer_url') || searchParams.get('customizerUrl') || searchParams.get('neatolabs')
  if (urlParam) return urlParam

  const hash = window.location.hash
  const match = hash.match(/#neatolabs=(.+)/)
  if (match) return match[1]
  return import.meta.env.VITE_3D_PLUGIN_V2
}

const buildCustomizerUrl = (baseUrl: string, params: Record<string, string | undefined>) => {
  const url = new URL(baseUrl)
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value ?? 'undefined')
  })
  return url.toString()
}

const getOrigin = (url: string | null) => {
  if (!url) return '*'
  try {
    return new URL(url).origin
  } catch {
    return '*'
  }
}

const hasOwn = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key)

const loadAllProductData = async (): Promise<ProductData[]> => {
  const { products } = snapshot(store)
  if (products.length) return JSON.parse(JSON.stringify(products))
  return (await getAllProducts()) || []
}

const ThemeCustomizerModal = ({ theme, sku, isOpen, onClose, onThemeSaved }: ThemeCustomizerModalProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeUrl, setIframeUrl] = useState<string | null>(null)

  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      const iframeWindow = iframeRef.current?.contentWindow
      if (!iframeWindow || event.source !== iframeWindow) return

      const iframeOrigin = getOrigin(iframeUrl)
      if (iframeOrigin !== '*' && event.origin !== iframeOrigin) return

      const data = event.data
      if (!data) return
      const message = typeof data === 'object' ? (data as Record<string, any>) : null

      if (message?.action === 'getAllProduct') {
        try {
          const products = await loadAllProductData()
          iframeWindow.postMessage(
            {
              action: 'getAllProduct',
              type: 'getAllProduct',
              requestId: message.requestId || message.id,
              products,
              data: products,
            },
            iframeOrigin
          )
        } catch (error: any) {
          iframeWindow.postMessage(
            {
              action: 'getAllProduct',
              type: 'getAllProduct',
              requestId: message.requestId || message.id,
              products: [],
              data: [],
              error: error?.message || 'Failed to load products',
            },
            iframeOrigin
          )
        }
        return
      }

      if (data === 'close-modal' || message?.type === 'close-modal' || message?.['close-modal']) {
        onClose()
        return
      }

      if (data === 'save-theme' || message?.type === 'save-theme' || (message && hasOwn(message, 'save-theme'))) {
        const rawTheme =
          message && hasOwn(message, 'save-theme') ? message['save-theme'] : message?.theme || message?.data?.theme
        const title = message ? message.title || message.data?.title || theme.title : theme.title

        if (!rawTheme) {
          notifyError('Theme save failed: no theme data received')
          iframeWindow.postMessage({ 'theme-saved': false, error: 'No theme data received' }, iframeOrigin)
          return
        }

        const saveResult = await updateTheme({ id: theme.id, title, theme: rawTheme })
        if (!saveResult) {
          notifyError('Theme save failed')
          iframeWindow.postMessage({ 'theme-saved': false, error: 'Theme save failed' }, iframeOrigin)
          return
        }

        iframeWindow.postMessage({ 'theme-saved': true }, iframeOrigin)
        onThemeSaved?.()
        return
      }
    },
    [theme, iframeUrl, onClose, onThemeSaved]
  )

  useEffect(() => {
    if (!isOpen) return
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isOpen, handleMessage])

  useEffect(() => {
    if (!isOpen) {
      setIframeUrl(null)
      return
    }
    const buildUrl = async () => {
      const baseUrl = getBaseUrl()
      const { currentUser } = snapshot(store)
      const storeName = currentUser?.selectedCompany?.store_name || ''
      const url = buildCustomizerUrl(baseUrl, {
        sku: sku || undefined,
        store_name: storeName,
        development: 'true',
        themeEditor: 'true',
        orderPage: 'false',
        theme: theme.id,
      })
      setIframeUrl(url)
    }
    buildUrl()
  }, [isOpen, theme.id, sku])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] bg-white">
      {iframeUrl ? (
        <iframe
          ref={iframeRef}
          id="theme-editor-iframe"
          src={iframeUrl}
          className="w-full h-full border-0 bg-white"
          allow="clipboard-read; clipboard-write"
        />
      ) : (
        <div className="flex items-center justify-center w-full h-full bg-white text-gray-500">Loading...</div>
      )}
    </div>
  )
}

export default ThemeCustomizerModal
